/**
 * `<TableFooterPopover>` — a second BubbleMenu pinned to the BOTTOM
 * edge of the active table. Holds table-LEVEL controls (operations
 * that change the table as a whole), as the sibling of the existing
 * `<TableBubbleMenu>` (above the cell, holds cell + row + column
 * structural ops).
 *
 * v1 controls (in priority order, left-to-right):
 *   1. Header-row background color picker — drives `headerBgColor`
 *      on the `WikiTable` node. Defaults to `null`, which paints the
 *      header TRANSPARENT so it reads like the body cells and is set
 *      apart only by its bold text — a deliberate choice against an
 *      implicit grey fill on every new table. Picker uses the unified
 *      `CUSTOM_PANEL_COLORS` palette so the table tint shares its
 *      identity with the panel kinds.
 *   2. Copy table — puts the WHOLE table on the clipboard, in both
 *      the rich `text/html` and the GFM `text/plain` halves, by
 *      selecting every cell and letting the browser's own copy run.
 *      See `copyTableToClipboard`.
 *   3. Delete table.
 *
 * Designed-for-extension: the popover is a horizontal row of icon
 * buttons. Future ops (row striping, export-to-CSV) land as new
 * buttons appended to the row OR — if the row gets busy — behind a
 * kebab menu at the end. The header-color picker uses a
 * lightweight inline grid (not a portal) so the popover stays a
 * single DOM subtree under tippy's host.
 *
 * Why a SECOND BubbleMenu instead of expanding the existing one?
 *   - Different anchor: cell ops attach to the cell (top-of-cell);
 *     table ops attach to the table (bottom-of-table). Mixing them
 *     in one menu leaves the table-level ops floating over the
 *     active cell and decoupled from the table outline.
 *   - Different surface size: the existing cell menu is a tight
 *     7-button row; the table menu carries a color picker that
 *     expands inline. Two menus stay readable; one becomes a
 *     mini-toolbar that visually competes with the wiki page's
 *     real toolbar.
 *
 * `shouldShow` predicate fires only when the editor is in a table
 * AND not in the middle of a multi-cell drag-select (the CellSelection
 * state has its own visual feedback; we'd be doubling up).
 */

import { BubbleMenu, type Editor } from '@tiptap/react'
import type { TFunction } from 'i18next'
import { Copy, Trash2 } from 'lucide-react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { useToast } from '@/components/ToastProvider'
import {
  CUSTOM_PANEL_COLORS,
  CUSTOM_PANEL_COLOR_SWATCH_HEX,
  type CustomPanelColor,
} from '@/editor/nodes/panels/panel-kinds'

import {
  copyTableToClipboard,
  isTableEdgeInView,
  tableMenuPopperOptions,
  tableMenuReferenceRect,
} from './active-table'
import type { TableHeaderBgColor } from './table-extensions'

interface TableFooterPopoverProps {
  editor: Editor
  t: TFunction<'common'>
}

/**
 * Read the live `headerBgColor` from the table node containing the
 * selection. Returns `null` when no table is active or the attribute
 * isn't set.
 *
 * Walks from the resolved selection start position up to the doc root,
 * looking for the first `table` ancestor. The TipTap `editor.isActive`
 * check is cheaper but doesn't surface attribute values; this walk does.
 */
function readActiveHeaderBgColor(editor: Editor): TableHeaderBgColor {
  const { selection } = editor.state
  const $from = selection.$from
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d)
    if (node.type.name === 'table') {
      const attr = node.attrs.headerBgColor
      if (typeof attr === 'string' && (CUSTOM_PANEL_COLORS as readonly string[]).includes(attr)) {
        return attr as CustomPanelColor
      }
      return null
    }
  }
  return null
}

/**
 * Find the document position of the active `table` ancestor, so we
 * can dispatch an `updateAttributes`-equivalent transaction targeting
 * it specifically. TipTap's `updateAttributes('table', ...)` chains
 * walk the resolved position and target the nearest match — so we can
 * lean on that command without computing the position ourselves.
 *
 * Returns whether the update fired (false when no table ancestor).
 */
function setTableHeaderBgColor(editor: Editor, color: TableHeaderBgColor): boolean {
  // `updateAttributes('table', { headerBgColor })` resolves the
  // nearest `table` ancestor of the current selection. Returns false
  // if the selection isn't inside a table; in that case the popover
  // shouldn't be visible anyway (shouldShow guards it).
  return editor.chain().focus().updateAttributes('table', { headerBgColor: color }).run()
}

export function TableFooterPopover({ editor, t }: TableFooterPopoverProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const toast = useToast()

  // Refs + flip-direction state for the smart-positioning sweep — the
  // BubbleMenu floats at the table's bottom-center, so the picker would
  // otherwise clip below the viewport when the table sits near the
  // bottom of the screen. Sticky decision so a small viewport shift
  // doesn't bounce the picker mid-interaction (mirrors the pattern in
  // MentionSuggestionPopover.tsx).
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const pickerRef = useRef<HTMLDivElement | null>(null)
  const [flipAbove, setFlipAbove] = useState(false)

  useLayoutEffect(() => {
    if (!pickerOpen) {
      setFlipAbove(false)
      return
    }
    const trigger = triggerRef.current
    const picker = pickerRef.current
    if (!trigger || !picker) return
    const triggerRect = trigger.getBoundingClientRect()
    const pickerHeight = picker.offsetHeight
    const GAP_PX = 8
    const spaceBelow = window.innerHeight - triggerRect.bottom
    const spaceAbove = triggerRect.top
    setFlipAbove(spaceBelow < pickerHeight + GAP_PX && spaceAbove > spaceBelow)
  }, [pickerOpen])

  const handlePickColor = useCallback(
    (color: TableHeaderBgColor) => {
      setTableHeaderBgColor(editor, color)
      setPickerOpen(false)
    },
    [editor],
  )

  const handleCopyTable = useCallback(() => {
    if (copyTableToClipboard(editor)) {
      toast.success(t('editor.tableFooter.copyTableDone'))
    } else {
      toast.error(t('editor.tableFooter.copyTableFailed'))
    }
  }, [editor, t, toast])

  const handleDeleteTable = useCallback(() => {
    editor.chain().focus().deleteTable().run()
  }, [editor])

  // Read on every render so the swatch reflects the current table's
  // attribute (e.g. cursor moved to a different table with a
  // different color). `BubbleMenu` re-renders on selection change,
  // so this stays current.
  const activeColor = readActiveHeaderBgColor(editor)

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        placement: 'bottom',
        duration: 100,
        // Pin the popover to the BOTTOM-CENTER of the whole table (not
        // the focused cell). The cell bubble menu anchors to the cell
        // at `placement: top`; this footer toolbar anchors to the
        // table as a single unit so it reads as table-level chrome.
        // Without this override, tippy uses the selection rect (the
        // focused cell), which drifts as the user moves between cells
        // and floats below random cells rather than under the table.
        // We return a zero-width rect at the midpoint of the table's
        // bottom edge so `placement: 'bottom'` (+ the `offset` below)
        // centers the popover horizontally and sits it 8px clear.
        getReferenceClientRect: () => tableMenuReferenceRect(editor, 'bottom'),
        offset: [0, 8],
        popperOptions: tableMenuPopperOptions(editor),
        // Close the inline color picker when the popover hides
        // (selection moved out of the table) so a re-show starts
        // clean. Tippy fires onHide before the DOM hides; reset
        // state synchronously.
        onHide: () => setPickerOpen(false),
      }}
      shouldShow={({ editor: e, state }) => {
        // See the note on the cell menu: a custom predicate replaces TipTap's
        // default, which is where the editability check normally lives.
        if (!e.isEditable) return false
        if (!e.isActive('table')) return false
        // Don't let the menu escape the field it belongs to when the
        // description is scrolled — see `isTableEdgeInView`.
        if (!isTableEdgeInView(e, 'bottom')) return false
        // Hide while a multi-cell selection is being dragged out —
        // CellSelection draws its own visual cue and a second menu
        // floating below would compete with it. The PM `Selection`
        // surfaces `ranges` as a readonly tuple; we only count its
        // length, so a structural cast through `unknown` keeps the
        // readonly-vs-mutable type variance Biome enforces happy.
        const sel = state.selection as unknown as { ranges?: ReadonlyArray<unknown> }
        if (sel.ranges && sel.ranges.length > 1) return false
        return true
      }}
      className="flex items-center gap-0.5 rounded-md border border-border bg-bg-elevated p-1 shadow-md"
    >
      {/* Header-row background color */}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setPickerOpen((open) => !open)}
          className="inline-flex items-center gap-1.5 rounded p-1.5 text-fg hover:bg-bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label={t('editor.tableFooter.headerBg')}
          aria-haspopup="true"
          aria-expanded={pickerOpen}
          title={t('editor.tableFooter.headerBg')}
        >
          <HeaderBgIcon />
          <span
            aria-hidden="true"
            className="h-3.5 w-3.5 rounded-full border border-border"
            style={{
              backgroundColor: activeColor
                ? CUSTOM_PANEL_COLOR_SWATCH_HEX[activeColor]
                : 'transparent',
            }}
          />
        </button>
        {pickerOpen ? (
          <div
            ref={pickerRef}
            // biome-ignore lint/a11y/useSemanticElements: swatch picker uses CSS Grid for layout; role="grid" is the structurally accurate ARIA primitive — same pattern as PanelPicker.tsx's icon grid.
            role="grid"
            aria-label={t('editor.tableFooter.headerBgPickerAria')}
            // Flip up when there isn't room below the trigger (the
            // BubbleMenu floats at the table's bottom-center, so a
            // table near the viewport bottom would otherwise clip the
            // picker). Sticky decision computed in the useLayoutEffect.
            //
            // Explicit fixed-width columns are LOAD-BEARING. `grid-cols-5`
            // alone translates to `repeat(5, minmax(0, 1fr))` — tracks
            // collapse to 0 when the parent doesn't supply a width, and
            // the `w-6` swatches end up stacked / overlapping. Pinning
            // each column to `1.5rem` (24px) makes the grid size to
            // exactly `5 × 24 + 4 × 6 (gap) + 2 × 8 (p-2) = 160px` —
            // which is what the spec called for.
            //
            // `max-h-[calc(100vh-32px)] overflow-y-auto` is the safety
            // net for extreme small-viewport cases.
            className={`absolute start-0 z-50 grid grid-cols-[repeat(5,1.5rem)] gap-1.5 rounded-md border border-border bg-bg-elevated p-2 shadow-md max-h-[calc(100vh-32px)] overflow-y-auto ${
              flipAbove ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            {/* "No tint" / clear — first grid cell, stays inline (Western
                scan order: top-left = "remove formatting"). */}
            <button
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: ARIA gridcell pattern matches the role="grid" container; <button> is the native interactive element underneath, role layered for screen-reader structure.
              role="gridcell"
              onClick={() => handlePickColor(null)}
              aria-label={t('editor.tableFooter.headerBgNone')}
              aria-pressed={activeColor === null}
              title={t('editor.tableFooter.headerBgNone')}
              className={`inline-flex h-6 w-6 items-center justify-center rounded-full border border-border text-fg-muted hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                activeColor === null
                  ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg-elevated'
                  : ''
              }`}
            >
              <span aria-hidden="true" className="text-xs leading-none">
                ×
              </span>
            </button>
            {CUSTOM_PANEL_COLORS.map((color) => {
              const isActive = activeColor === color
              return (
                <button
                  key={color}
                  type="button"
                  role="gridcell"
                  onClick={() => handlePickColor(color)}
                  aria-label={t('editor.tableFooter.headerBgSwatch', { color })}
                  aria-pressed={isActive}
                  title={color}
                  // Subtle `hover:ring-1 hover:ring-fg/20` signals
                  // interactivity without competing with the swatch fill.
                  // Active = outer accent ring with 1px offset, replacing
                  // the legacy inner white ✓ glyph (failed WCAG AA non-
                  // text contrast on every "300-tier" swatch).
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full border border-border hover:ring-1 hover:ring-fg/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                    isActive ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg-elevated' : ''
                  }`}
                  style={{ backgroundColor: CUSTOM_PANEL_COLOR_SWATCH_HEX[color] }}
                />
              )
            })}
          </div>
        ) : null}
      </div>

      <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-border" />

      <button
        type="button"
        onClick={handleCopyTable}
        className="inline-flex items-center justify-center rounded p-1.5 text-fg hover:bg-bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label={t('editor.tableFooter.copyTable')}
        title={t('editor.tableFooter.copyTable')}
      >
        <Copy className="h-4 w-4" aria-hidden="true" />
      </button>

      <button
        type="button"
        onClick={handleDeleteTable}
        className="inline-flex items-center justify-center rounded p-1.5 text-fg hover:bg-bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label={t('editor.tableFooter.deleteTable')}
        title={t('editor.tableFooter.deleteTable')}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>
    </BubbleMenu>
  )
}

/**
 * Header-row tint glyph — a small grid where the top row is filled.
 * Inline SVG keeps the bundle lean (the existing table icons use the
 * same approach). 16x16 to match the Lucide row icons rendered next
 * to it.
 */
function HeaderBgIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <title>Header row color</title>
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <rect x="3" y="3" width="18" height="6" fill="currentColor" stroke="none" opacity="0.35" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  )
}
